import { observer } from 'mobx-react';
import { FC, useCallback } from 'react';
import configWebStore from '../../stores';
import { IConfigWebConfiguration } from '../../types';
import { LOCALES } from 'src/constants/main';
import { countriesStore } from 'src/mobx/countriesStore';
import { localeStore } from 'src/mobx/localesStore';
import AddressSearch from 'src/view/components/AddressSearch/AddressSearch';
import Input from 'src/view/components/Controls/InputComponent/Input';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import PhoneInput from 'src/view/components/Controls/PhoneInput/PhoneInput';
import Text, { TEXT } from 'src/view/components/Text/Text';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import { TBooleanInNumber } from '~/types/main';
import './style.scss';

const WebConfigContactBlock: FC = observer((): JSX.Element => {
  const { email, mobile, copyright, address, isMobileHidden } = configWebStore.configuration.webContact;

  const changeKeyword = useCallback(
    (text: string, locales: LOCALES) => {
      configWebStore.changeConfigWebContact({
        businessName: { ...configWebStore.configuration.webContact.businessName, [locales]: text },
      });
    },
    [configWebStore.configuration.webContact.businessName]
  );

  const changeWebContact = (field: keyof IConfigWebConfiguration['webContact']) => (value: string | undefined) => {
    configWebStore.changeConfigWebContact({ [field]: value });
  };

  const changeMobileVisibility = (status: TBooleanInNumber) => {
    configWebStore.changeConfigWebContact({ isMobileHidden: status });
  };

  const changeAddress = useCallback((value: string | undefined) => {
    configWebStore.changeConfigWebContact({ address: value });
  }, []);

  return (
    <div className="d-flex flex-column gap-20">
      <Text type={TEXT.SECTION_HEADER} text="website_contact" className="mt-6" />

      <div className="web-config-contact-block__wrapper">
        <div className="d-flex flex-column gap-15">
          <div className="web-config-contact-block align-items-start">
            <Text type={TEXT.ORDER_FIELD} text="business_name" required className="web-config-contact-block__title" />
            <MultiLanguageInput
              enText={configWebStore.configuration.webContact.businessName.en}
              arText={configWebStore.configuration.webContact.businessName.ar}
              onChangeValue={changeKeyword}
              validation={configWebStore.contactValidation.businessName}
              placeholder={localeStore.t('Value')}
              className="web-config-meta-block__input"
            />
          </div>

          <div className="web-config-contact-block">
            <Text type={TEXT.ORDER_FIELD} text="hint_email_id" required className="web-config-contact-block__title" />
            <Input
              value={email}
              onChangeValue={changeWebContact('email')}
              validation={configWebStore.contactValidation.email}
              placeholder="email@mail.com"
              contentClassName="web-config-meta-block__input"
            />
          </div>

          <div className="web-config-contact-block">
            <Text type={TEXT.ORDER_FIELD} text="txt_mobile" required className="web-config-contact-block__title" />
            <PhoneInput
              value={mobile}
              onChange={changeWebContact('mobile')}
              onlyCountries={countriesStore.countriesForPhoneInput}
              error={!configWebStore.contactValidation.mobile.isValid}
              className="web-config-meta-block__input"
            />

            <div className="d-flex align-items-center ml-5 switcher-container">
              <Switcher status={isMobileHidden} onChange={changeMobileVisibility} />
              <Text
                type={TEXT.SWITCHER_LABEL}
                text={localeStore.t('hide_mobile_checkbox_header')}
                className="text-nowrap"
                tooltip={{ message: localeStore.t('hide_mobile_checkbox_infoicon_text') }}
              />
            </div>
          </div>

          <div className="web-config-contact-block">
            <Text type={TEXT.ORDER_FIELD} text="copyright" className="web-config-contact-block__title" />
            <Input
              value={copyright}
              onChangeValue={changeWebContact('copyright')}
              placeholder={localeStore.t('Value')}
              contentClassName="web-config-meta-block__input"
            />
          </div>

          <div className="web-config-contact-block">
            <Text type={TEXT.ORDER_FIELD} text="txt_address" className="web-config-contact-block__title" />
            <AddressSearch value={address} onChangeAddress={changeAddress} className="web-config-contact-block__input" />
          </div>
        </div>
      </div>
    </div>
  );
});

export default WebConfigContactBlock;
